// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { useFlippers } from "@shared/context/FlippersContext";
import { BackButton } from "@shared/layouts";
import { useLocation } from "@shared/react-router-dom/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { PoweredByFullscript } from "@unauthenticated/signupSignin/components/PoweredByFullscript/PoweredByFullscript";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpSubtitle } from "@unauthenticated/signupSignin/components/SignUpSubtitle/SignUpSubtitle";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import type { LocationType } from "@unauthenticated/signupSignin/data/types/PatientAuth";
import { l } from "@unauthenticated/signupSignin/locales/i18n";
import { PasswordForm } from "@unauthenticated/signupSignin/scenes/PatientAuthPasswordPage/PasswordForm/PasswordForm";

import * as sharedStyles from "../storeLandingScenes.styles";

import * as styles from "./PatientAuthPasswordPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  storeId?: string;
  activePatientPromotion?: boolean;
  isStorefronts?: boolean;
}

const PatientAuthPasswordPage = ({
  patientAttributes,
  additionalAttributes,
  storeId,
  activePatientPromotion,
  isStorefronts,
}: Props) => {
  const [isQualityStorySignupEnabled] = useFlippers("platypus_quality_story_signup");
  const { t } = useTranslation();
  const formVersion = "StoreSignInPage";
  const {
    storeLogo,
    organizationLogo,
    storeName,
    treatmentPlanSlug,
    uuid,
    storeSlug,
    pracAvatar,
    iconLogo,
  } = patientAttributes;
  const FORGOT_PASSWORD_LINK = authRoutes.welcomeForgotPassword(
    storeSlug,
    uuid || treatmentPlanSlug
  );
  const { state }: LocationType = useLocation();

  const signUpTitle = isStorefronts ? t(l.signIn.signInAccount) : storeName;
  const showQualityStoryChrome = !isStorefronts && !!isQualityStorySignupEnabled;

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      storeSlug={storeSlug}
      pracAvatar={pracAvatar}
      isActivePromotion={activePatientPromotion}
      isStorefronts={isStorefronts}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
      hasFullBleedPanel
    >
      <>
        <SignUpHeader isStorefronts={isStorefronts} hasCompactSpacing={showQualityStoryChrome}>
          {!!isStorefronts && (
            <div css={styles.backButtonWrapper}>
              <BackButton text={t(l.common.Back)} isUnderLined={false} />
            </div>
          )}
          {!isStorefronts && <StoreLogo storeLogo={storeLogo} storeName={storeName} />}
          <SignUpTitle title={signUpTitle} />
          {!!showQualityStoryChrome && (
            <SignUpSubtitle subtitle={t(l.patientRxAuth.QualityStorySubtitle)} />
          )}
        </SignUpHeader>
        {showQualityStoryChrome ? (
          <div css={sharedStyles.headerSpacer} />
        ) : (
          <Spacer height={isStorefronts ? "half" : "double"} />
        )}
        <PasswordForm
          isStorefronts={isStorefronts}
          additionalAttributes={{ ...additionalAttributes, formVersion }}
          email={state?.email}
          forgotPasswordLink={FORGOT_PASSWORD_LINK}
          storeId={storeId}
        />
        {showQualityStoryChrome ? <PoweredByFullscript /> : <Spacer height="oneHalf" />}
      </>
    </StoreLandingLayout>
  );
};

export { PatientAuthPasswordPage };
